/**
 * Generation token for in-flight `<video>.play()` so pause / hold / hide / unmount
 * can invalidate stale attempts (Chrome AbortError races on clip transitions).
 *
 * Play attempts **capture** the current epoch (they do not bump). Only tear-down paths
 * bump via {@link invalidateVideoPlayEpoch}, so concurrent `playVideo` calls for the same
 * on-air intent do not cancel each other.
 */

const PLAY_EPOCH_KEY = '__lucimarkVideoPlayEpoch'
const PLAY_INTENT_KEY = '__lucimarkVideoPlayIntent'
const ENSURE_PLAY_PENDING_KEY = '__lucimarkVideoEnsurePlayPending'
const ENSURE_PLAY_ATTEMPTS_KEY = '__lucimarkVideoEnsurePlayAttempts'

/** Cap coalesced ensure re-entries per tear-down cycle (avoid AbortError microtask loops). */
export const MAX_VIDEO_PLAY_ENSURE_ATTEMPTS = 2

export interface VideoPlayIntent {
  show: boolean
  presentationHoldOverlay: boolean
}

function bag(el: HTMLVideoElement): Record<string, unknown> {
  return el as unknown as Record<string, unknown>
}

export function getVideoPlayEpoch(el: HTMLVideoElement): number {
  const n = bag(el)[PLAY_EPOCH_KEY]
  return typeof n === 'number' && Number.isFinite(n) ? n : 0
}

/** Bump epoch; any in-flight play that captured the previous value becomes stale. */
export function bumpVideoPlayEpoch(el: HTMLVideoElement): number {
  const next = getVideoPlayEpoch(el) + 1
  bag(el)[PLAY_EPOCH_KEY] = next
  return next
}

export function isVideoPlayEpochCurrent(el: HTMLVideoElement, epoch: number): boolean {
  return getVideoPlayEpoch(el) === epoch
}

function resetEnsureAttempts(el: HTMLVideoElement): void {
  bag(el)[ENSURE_PLAY_ATTEMPTS_KEY] = 0
  bag(el)[ENSURE_PLAY_PENDING_KEY] = false
}

/** Call before pause / tear-down so pending `play()` settles as stale. */
export function invalidateVideoPlayEpoch(el: HTMLVideoElement): number {
  resetEnsureAttempts(el)
  return bumpVideoPlayEpoch(el)
}

export function setVideoPlayIntent(el: HTMLVideoElement, intent: VideoPlayIntent): void {
  bag(el)[PLAY_INTENT_KEY] = intent
}

export function getVideoPlayIntent(el: HTMLVideoElement): VideoPlayIntent | undefined {
  const v = bag(el)[PLAY_INTENT_KEY]
  if (v == null || typeof v !== 'object') {
    return undefined
  }
  const show = (v as VideoPlayIntent).show
  const hold = (v as VideoPlayIntent).presentationHoldOverlay
  if (typeof show !== 'boolean' || typeof hold !== 'boolean') {
    return undefined
  }
  return { show, presentationHoldOverlay: hold }
}

/**
 * Whether this element should still attempt / finish a play for presentation.
 * Stale intent, sticky hold, hidden slot, or detached DOM → false.
 */
export function shouldAttemptVideoPlay(el: HTMLVideoElement): boolean {
  if (!el.isConnected) {
    return false
  }
  const intent = getVideoPlayIntent(el)
  if (intent == null) {
    return false
  }
  if (!intent.show || intent.presentationHoldOverlay) {
    return false
  }
  return true
}

/** True when on-air intent still wants playback but the element is not playing. */
export function shouldEnsureVideoPlaying(
  el: HTMLVideoElement,
  isCurrentlyPlaying: boolean
): boolean {
  return shouldAttemptVideoPlay(el) && !isCurrentlyPlaying
}

/** Clear ensure budget after a successful on-air play settles. */
export function markVideoPlayEnsureSucceeded(el: HTMLVideoElement): void {
  resetEnsureAttempts(el)
}

/**
 * Coalesce one microtask re-entry into `play` when a prior attempt exited stale/benign
 * while intent still wants on-air playback (avoids silent slots after raced updates).
 * Capped at {@link MAX_VIDEO_PLAY_ENSURE_ATTEMPTS} per tear-down cycle.
 */
export function scheduleEnsureVideoPlaying(
  el: HTMLVideoElement,
  play: () => void
): boolean {
  if (!shouldAttemptVideoPlay(el)) {
    return false
  }
  if (bag(el)[ENSURE_PLAY_PENDING_KEY] === true) {
    return false
  }
  const attemptsRaw = bag(el)[ENSURE_PLAY_ATTEMPTS_KEY]
  const attempts = typeof attemptsRaw === 'number' && Number.isFinite(attemptsRaw) ? attemptsRaw : 0
  if (attempts >= MAX_VIDEO_PLAY_ENSURE_ATTEMPTS) {
    return false
  }
  bag(el)[ENSURE_PLAY_PENDING_KEY] = true
  queueMicrotask(() => {
    bag(el)[ENSURE_PLAY_PENDING_KEY] = false
    if (!shouldAttemptVideoPlay(el)) {
      return
    }
    const prevRaw = bag(el)[ENSURE_PLAY_ATTEMPTS_KEY]
    const prev = typeof prevRaw === 'number' && Number.isFinite(prevRaw) ? prevRaw : 0
    bag(el)[ENSURE_PLAY_ATTEMPTS_KEY] = prev + 1
    play()
  })
  return true
}

export function clearVideoPlayEpochState(el: HTMLVideoElement): void {
  delete bag(el)[PLAY_EPOCH_KEY]
  delete bag(el)[PLAY_INTENT_KEY]
  delete bag(el)[ENSURE_PLAY_PENDING_KEY]
  delete bag(el)[ENSURE_PLAY_ATTEMPTS_KEY]
}
